import { useRef, useCallback, useState } from 'react';
import { GameEngine } from '../logic/gameLogic';
import { GameState, GameAction } from '../types/game';

export interface UseGameStateReturn {
  gameState: GameState | null;
  playerId: 1 | 2 | null;
  isSpectator: boolean;
  spectatorId: number | null;
  isHost: boolean;

  // Actions
  initGame: (seed: number, roomId: string) => GameState;
  loadState: (state: GameState) => void;
  executeAction: (playerId: 1 | 2, action: GameAction) => boolean;
  setPlayerId: (id: 1 | 2 | null) => void;
  setIsHost: (isHost: boolean) => void;
  setSpectator: (spectatorId: number) => void;
  getEngine: () => GameEngine | null;
  reset: () => void;
}

export function useGameState(): UseGameStateReturn {
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [playerId, setPlayerIdState] = useState<1 | 2 | null>(null);
  const [isSpectator, setIsSpectator] = useState(false);
  const [spectatorId, setSpectatorIdState] = useState<number | null>(null);
  const [isHost, setIsHostState] = useState(false);

  const gameEngineRef = useRef<GameEngine | null>(null);

  const initGame = useCallback((seed: number, roomId: string): GameState => {
    const engine = new GameEngine(seed, roomId);
    gameEngineRef.current = engine;
    const state = engine.getState();
    setGameState(state);
    return state;
  }, []);

  const loadState = useCallback((state: GameState) => {
    if (!gameEngineRef.current) {
      const engine = new GameEngine(state.seed, state.roomId);
      engine.loadState(state);
      gameEngineRef.current = engine;
    } else {
      gameEngineRef.current.loadState(state);
    }
    setGameState(state);
  }, []);

  const executeAction = useCallback((pid: 1 | 2, action: GameAction): boolean => {
    const engine = gameEngineRef.current;
    if (!engine) return false;

    const success = engine.executeAction(pid, action);
    if (success) {
      setGameState(engine.getState());
    }
    return success;
  }, []);

  const setPlayerId = useCallback((id: 1 | 2 | null) => {
    setPlayerIdState(id);
    if (id !== null) {
      setIsSpectator(false);
      setSpectatorIdState(null);
    }
  }, []);

  const setIsHost = useCallback((host: boolean) => {
    setIsHostState(host);
  }, []);

  const setSpectator = useCallback((sid: number) => {
    setIsSpectator(true);
    setSpectatorIdState(sid);
    setPlayerIdState(null);
  }, []);

  const getEngine = useCallback(() => gameEngineRef.current, []);

  const reset = useCallback(() => {
    gameEngineRef.current = null;
    setGameState(null);
    setPlayerIdState(null);
    setIsSpectator(false);
    setSpectatorIdState(null);
    setIsHostState(false);
  }, []);

  return {
    gameState,
    playerId,
    isSpectator,
    spectatorId,
    isHost,
    initGame,
    loadState,
    executeAction,
    setPlayerId,
    setIsHost,
    setSpectator,
    getEngine,
    reset,
  };
}
